import React, { useContext, useLayoutEffect, useRef, useState } from "react";
import { Box, Dialog, Divider, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import ListResponsive from "../../../../components/other/ListResponsive";
import {
  AdepticonTeamRow,
  DoublesRow,
  PlayerRow,
  SeatedRow,
  TeamRow
} from "./EventPairingRows";
import { AuthContext } from "../../../../components/user/BCPAuthProvider";

export default function EventPairingsFullscreen(props) {
  const { t } = useTranslation("translation", {
    keyPrefix: "components.EventPairings"
  });

  const { event, pairings, roundNo, organize, open, setOpen } = props;
  const pauseTime = 3000;

  const authContext = useContext(AuthContext);
  const theme = useTheme();
  const containerRef = useRef(null);

  const [scrollStart, setScrollStart] = useState(false);

  useLayoutEffect(() => {
    if (!open) return;

    const delayTimer = setTimeout(() => {
      setScrollStart(true);
    }, pauseTime);

    return () => clearTimeout(delayTimer);
  }, [open]);

  useLayoutEffect(() => {
    if (!scrollStart) return;

    const container = containerRef.current;
    if (!container) return;
    const scrollSpeed = 3;

    let scrollDirection = "down";

    const scroll = () => {
      if (scrollDirection === "down") {
        container.scrollTop += scrollSpeed;
        if (
          container.scrollTop + container.clientHeight >=
          container.scrollHeight
        ) {
          scrollDirection = "pauseDown";
          setTimeout(() => (scrollDirection = "up"), pauseTime);
        }
      } else if (scrollDirection === "up") {
        container.scrollTop -= scrollSpeed;
        if (container.scrollTop <= 0) {
          scrollDirection = "pauseUp";
          setTimeout(() => (scrollDirection = "down"), pauseTime);
        }
      }
    };

    const interval = setInterval(scroll, 50);

    return () => clearInterval(interval);
  }, [scrollStart]);

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={() => setOpen(false)}
      sx={{
        "& .MuiPaper-root": {
          alignItems: "center",
          textAlign: "center"
        }
      }}
      onClick={() => setOpen(false)}
    >
      <Box width={"100%"} zIndex={99}>
        <Typography
          variant="h1"
          sx={{ fontSize: ["10vw", "6vw", "4vw", "3vw"] }}
        >
          {event?.name}
        </Typography>
        <Divider />
        {(roundNo >= 1 || !event.status?.started) && (
          <Typography
            variant="h2"
            sx={{
              fontSize: ["8vw", "5vw", "3vw", "2vw"],
              mt: 2,
              mb: 2
            }}
          >
            {t("round")} {roundNo}
          </Typography>
        )}
      </Box>
      <Box
        ref={containerRef}
        sx={{
          width: "100%",
          maxHeight: "80vh",
          overflowY: "scroll",
          scrollbarColor:
            theme?.palette?.mode === "dark" ? "#6b6b6b #2b2b2b" : "",
          "&::-webkit-scrollbar, & *::-webkit-scrollbar": {
            backgroundColor: theme?.palette?.mode === "dark" ? "#2b2b2b" : ""
          }
        }}
      >
        <ListResponsive
          originalList={pairings}
          userRowsPerPage="pairings"
          rowProps={{
            headings: (
              <Typography
                variant="h4"
                color="text.secondary"
                gutterBottom
                textAlign="left"
                pl={1}
              >
                Table
              </Typography>
            ),
            mapFunction: (row, index) => {
              if (!organize && event?.adepticonTeamEvent) {
                return (
                  <AdepticonTeamRow
                    row={row}
                    event={event}
                    authContext={authContext}
                  />
                );
              } else if (row?.pairingType === "SeatedPairing") {
                return <SeatedRow row={row} />;
              } else if (event?.doublesEvent || event?.format?.doublesEvent) {
                return <DoublesRow row={row} event={event} />;
              } else if (event?.teamEvent || event?.format?.teamEvent) {
                return <TeamRow row={row} event={event} />;
              } else {
                return <PlayerRow row={row} event={event} key={index} />;
              }
            }
          }}
        />
      </Box>
    </Dialog>
  );
}
